<template>
    <v-card :hover="compareMode" variant="outlined" :class="{'elevation-0': !fullCard}" class="my-2">
        <v-list-item lines="three" class="pl-0">
            <template v-if="fullCard" v-slot:prepend>
                <v-img
                    tile
                    class="thumb my-0"
                    :src="$filters.imageUrl(mainImage.url, 200)"
                    :alt="offer.info.description"
                    :lazy-src="$filters.srcHost('/placeholder.png')"
                    :width="!$breakpoint.smAndDown ? '125px' : '25%'"
                    @error="onImgError"
                >
                    <template v-slot:placeholder>
                        <v-row
                            v-show="!imgError && mainImage.url"
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                        >
                            <v-progress-circular indeterminate color="grey-lighten-5" />
                        </v-row>
                    </template>
                </v-img>
            </template>
            <div class="d-flex flex-column flex-md-row">
                <div class="align-self-start" :class="{'pl-2': !fullCard}">
                    <div class="mb-1 d-flex">
                        <v-checkbox
                            v-if="compareMode && !offer.soldOut"
                            v-model="compareOfferKeys"
                            :value="offer.offerKey"
                            :ripple="false"
                            multiple
                            density="compact"
                            hide-details
                            class="mr-2"
                        />
                        <div
                            class="wrap-words d-flex align-center text-subtitle-1 font-weight-bold cursor-pointer"
                            @click="offerInfo()"
                        >
                            <v-icon size="small" start>mdi-information-outline</v-icon>
                            <span>{{ offer.info.description }}</span>
                            <ProductOfferTipsDialog v-if="offer.tipsAvailable" :offer-key="offer.offerKey" />
                            <span v-if="offerTime" class="ms-2 text-grey font-weight-bold text-body-2">
                                {{ `(${offerTime})` }}
                            </span>
                        </div>
                    </div>
                    <div class="d-flex align-center">
                        <div v-if="offer.info.passengerQuantity" class="mr-3 text-caption d-flex align-center">
                            <v-icon size="small" start>mdi-account</v-icon>
                            {{ offer.info.passengerQuantity }}
                        </div>
                        <div v-if="offer.info.baggageQuantity" class="mr-3 text-caption d-flex align-center">
                            <v-icon size="small" start>mdi-bag-checked</v-icon>
                            {{ offer.info.baggageQuantity }}
                        </div>
                    </div>
                    <div v-if="discountDetails" class="d-flex flex-shrink-0 width-max-content">
                        <special-offer-label :details="discountDetails" />
                    </div>
                    <div v-if="offer.available" class="d-flex align-center text-caption">
                        <v-icon :title="$t('available')" start size="small" class="text-success">
                            mdi-checkbox-marked-circle
                        </v-icon>
                        <span class="text-success" v-text="$t('available')" />
                    </div>
                    <div v-else class="d-flex align-center text-caption">
                        <v-icon :title="$t('on_request')" start size="small" class="text-warning"
                            >mdi-alert-circle</v-icon
                        >
                        <span class="text-warning" v-text="$t('on_request')" />
                    </div>
                    <span v-if="offer && offer.price && showPriceDuringSearch" class="text-caption">
                        <conditions-label
                            :offer-key="offer.offerKey"
                            :price="offer.price"
                            @conditions="showConditionsInfo"
                        />
                    </span>
                    <div v-if="offer.info.tariffName" class="d-flex align-center text-caption">
                        <v-icon start size="small" class="text-info">mdi-tag-text</v-icon>
                        <span>
                            {{ offer.info.tariffName }}
                        </span>
                    </div>
                    <transfer-driver-languages :lang-codes="offer.langCodes" />
                    <div v-if="isAddOn" class="d-flex align-center text-caption">
                        <v-icon :title="$t('date')" start size="small">mdi-calendar</v-icon>
                        <span>{{ $filters.dateFormat(offer.date) }}</span>
                    </div>
                </div>
                <v-spacer />
                <div class="offer-price align-self-end align-self-md-center d-flex flex-column align-center">
                    <slot name="price" :offer="offer" :offer-key="offer.offerKey">
                        <template v-if="!offer.soldOut">
                            <discount-label v-if="offer.price" :price="offer.price" class="mb-2" />
                            <v-btn
                                :loading="loading"
                                :disabled="loading"
                                color="primary"
                                height="36px"
                                max-height="36px"
                                block
                                variant="flat"
                                size="small"
                                data-cy="client-price"
                                @click="action(selectForBooking)"
                            >
                                {{ label($filters.price(offer.price)) }}
                            </v-btn>
                            <template v-if="showPriceDuringSearch">
                                <price-note :search-request="searchRequest" tourist-label="passenger" />
                                <commission-amount :commission="offer.price.commission" />
                                <net-amount :search-request="searchRequest" :price="offer.price" />
                            </template>
                        </template>
                        <sold-out-modal v-else small />
                    </slot>
                </div>
            </div>
        </v-list-item>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Setup, Vue} from 'vue-facing-decorator'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import DiscountLabel from '@/components/snippets/DiscountLabel.vue'
    import TransferInfoModal from '@/components/transfers/info/TransferInfoModal.vue'
    import ConditionsLabel from '~/components/snippets/ConditionsLabel.vue'
    import {EventBus, SHOW_TRANSFER_CONDITIONS_INFO, SHOW_TRANSFER_INFO_MODAL} from '@/utils/event-bus'
    import PriceNote from '@/components/snippets/PriceNote.vue'
    import TransferDriverLanguages from '@/components/snippets/TransferDriverLanguages.vue'
    import NetAmount from '~src/components/snippets/NetAmount.vue'
    import SpecialOfferLabel from '~/components/hotels/search/offers/SpecialOfferLabel.vue'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'
    import SoldOutModal from '~src/components/snippets/SoldOutModal.vue'

    @Component({
        components: {
            SoldOutModal,
            ProductOfferTipsDialog,
            SpecialOfferLabel,
            TransferDriverLanguages,
            PriceNote,
            CommissionAmount,
            DiscountLabel,
            TransferInfoModal,
            ConditionsLabel,
            NetAmount,
        },
    })
    export class TransferOfferCard extends Vue {
        @Prop({required: true}) offer
        @Prop({required: true}) productId
        @Prop({default: true}) fullCard
        @Prop() isReturnTransfer
        @Prop({default: false, type: Boolean}) isAddOn
        @Prop({default: false, type: Boolean}) compareMode
        @Setup(() => useBookingPriceButton().action) action
        @Setup(() => useBookingPriceButton().label) label
        @Setup(() => useBookingPriceButton().showPriceDuringSearch) showPriceDuringSearch

        imgError = false
        info = {
            images: [],
        }
        loading = true

        @Emit()
        selectTransferOffer() {
            return this.offer
        }

        async mounted() {
            try {
                this.info = await this.$api.transferInfo.get({offerKey: this.offer.offerKey})
                if (this.fullCard && this.info.images) {
                    this.$emit('images', this.info.images)
                }
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.loading = false
            }
        }

        onImgError() {
            if (this.mainImage.url) {
                this.imgError = true
            }
        }

        selectForBooking() {
            if (this.isReturnTransfer) {
                this.selectTransferOffer()
            } else {
                transfersStore.ADD_TO_BASKET({info: this.info, offerKey: this.offer.offerKey})
                this.$router.push({
                    name: 'transferBooking',
                    query: {offerKey: this.offer.offerKey},
                })
            }
        }

        offerInfo() {
            EventBus.$emit(SHOW_TRANSFER_INFO_MODAL, {offer: this.offer, info: this.info})
        }

        showConditionsInfo() {
            EventBus.$emit(SHOW_TRANSFER_CONDITIONS_INFO, {searchRequest: this.searchRequest, offer: this.offer})
        }

        get compareOfferKeys() {
            return transfersStore.offersToCompare.find(product => product.productId === this.productId)?.offersKeys
        }

        set compareOfferKeys(offersKeys) {
            transfersStore.ADD_PRODUCT_TO_COMPARE(this.productId, offersKeys)
        }

        get mainImage() {
            return transfersRuntimeStore.offerMainImage(this.offer, this.info)
        }

        get offerTime() {
            if (this.offer.time?.length) {
                return this.offer.time[0]
            }
            if (this.offer.departureTimeFrom && this.offer.departureTimeTo) {
                return [this.offer.departureTimeFrom, this.offer.departureTimeTo].join(' - ')
            }

            return null
        }

        get searchRequest() {
            return transfersStore.searchRequest
        }

        get isAgency() {
            return authStore.isAgency
        }

        get discountDetails() {
            return this.offer.price?.discountDetails
        }
    }

    export default TransferOfferCard
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .offer-price {
        width: 20%;
    }

    @media #{map.get(settings.$display-breakpoints, 'xs')} {
        .offer-price {
            width: 25%;
        }
    }

    .wrap-words {
        white-space: normal;
    }
</style>
